import React from 'react'
import {
	Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow, TableCell,
	Grid, Checkbox, IconButton
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { AppButtonIcon, AppCardTitle, AppRunProgress, AppRetailerBlockContainer } from '@components'
import AppOfficesBlockContainer from '@components/DataBlocks/OfficesContainer'
import { IconSeeker } from '@ui-seeker/core'
import { displayDay } from '@utils'
import { dsColorNew } from "@palette"


// Sample dates: today and yesterday, as the page shows today's batches by default
const isoDay = offset => { const d = new Date(); d.setDate(d.getDate() - offset); return [d.getFullYear(), d.getMonth() + 1, d.getDate()].map(n => String(n).padStart(2, '0')).join('-') }
const TODAY = isoDay(0)
const YESTERDAY = isoDay(1)
// Batch IDs as the real ones: B + creation timestamp + sequence + user
const batchId = (day, time, seq) => `B${day.replace(/-/g, '')}${time.replace(/:/g, '')}${seq}UI9135E`

const DEMO_BATCHES = [
	{
		id: batchId(TODAY, '09:15:32', '481231'),
		sites: ['PriceFly', 'RentalSpot'],
		offices: ['Madrid Airport (MAD)', 'Barcelona Airport (BCN)'],
		searches: 1240,
		created: { date: TODAY, time: '09:15:32' },
		status: 'running',
		progress: { complete: 876, total: 1240 },
		errors: 3,
		na: 12,
		hasExport: false,
	},
	{
		id: batchId(TODAY, '08:42:10', '482290'),
		sites: ['BookRide', 'AutoLink'],
		offices: ['Malaga Airport (AGP)'],
		searches: 680,
		created: { date: TODAY, time: '08:42:10' },
		status: 'ready',
		progress: { complete: 680, total: 680 },
		errors: 1,
		na: 5,
		hasExport: true,
	},
	{
		id: batchId(YESTERDAY, '14:30:05', '482254'),
		sites: ['PriceFly', 'SwiftFleet', 'RentalSpot'],
		offices: ['Palma de Mallorca Airport (PMI)', 'Ibiza Airport (IBZ)'],
		searches: 2100,
		created: { date: YESTERDAY, time: '14:30:05' },
		status: 'stopped',
		stoppedAt: { date: YESTERDAY, time: '15:12' },
		progress: { complete: 1450, total: 2100 },
		errors: 8,
		na: 34,
		hasExport: false,
	},
	{
		id: batchId(YESTERDAY, '11:05:48', '482207'),
		sites: ['RentalSpot'],
		offices: ['Valencia Center', 'Alicante Airport (ALC)'],
		searches: 420,
		created: { date: YESTERDAY, time: '11:05:48' },
		status: 'ready',
		progress: { complete: 420, total: 420 },
		errors: 0,
		na: 2,
		hasExport: false,
	},
	{
		id: batchId(YESTERDAY, '09:20:15', '482186'),
		sites: ['BookRide', 'PriceFly'],
		offices: ['Tenerife South Airport (TFS)'],
		searches: 560,
		created: { date: YESTERDAY, time: '09:20:15' },
		status: 'running',
		generating: true,
		progress: { complete: 560, total: 560 },
		errors: 2,
		na: 8,
		hasExport: false,
	},
]

// the real Sites / Offices columns take rows; airports unless the name says otherwise
const siteRows = sites => sites.map(name => ({ name, market: 'Spain' }))
const officeRows = offices => offices.map(name => ({ name, country: 'Spain', type: name.includes('Center') ? 'downtown' : 'airport' }))

const noop = () => {}


function StatusCounter({ icon, value, color, bg }) {
	return <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, height: 24, px: 1, borderRadius: '6px', fontSize: 12, fontWeight: 600, color, backgroundColor: bg }}>
		<IconSeeker icon={icon} iconWeight="regular" sx={{ fontSize: 12, color: 'inherit' }}/>
		{value}
	</Box>
}


// Same cell as Pages/Batch/Page/Table/columns/history.jsx
function StatusCell({ batch }) {
	return <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, width: '100%' }}>
		{batch.status === 'stopped'
			? <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
				<Box sx={{ fontSize: 13, color: dsColorNew.textTitle, whiteSpace: 'nowrap' }}>{displayDay(batch.stoppedAt.date)} {batch.stoppedAt.time}</Box>
				<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 12, fontWeight: 600, color: dsColorNew.dangerRed, backgroundColor: dsColorNew.dangerBg, borderRadius: '6px', px: 1, py: 0.25 }}>
					<IconSeeker icon="hand" sx={{ fontSize: 12, color: 'inherit' }}/> Stopped
				</Box>
			</Box>
			: <>
				<Box sx={{ fontSize: 13, color: dsColorNew.textTitle, whiteSpace: 'nowrap' }}>
					{displayDay(batch.created.date)} <Box component="span" sx={{ color: dsColorNew.textMuted }}>{batch.created.time}</Box>
				</Box>
				{batch.status === 'running' && <AppRunProgress
					actual={batch.progress.complete}
					total={batch.progress.total}
					done={batch.progress.complete}
					totalText={batch.progress.total}
					generating={batch.generating}
				/>}
			</>
		}

		<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
			<StatusCounter icon="triangle-exclamation" value={batch.errors} color={dsColorNew.noteAmber} bg={dsColorNew.noteAmberBg}/>
			<StatusCounter icon="calendar-circle-exclamation" value={batch.na} color={dsColorNew.dangerRed} bg={dsColorNew.dangerBg}/>
			<Box sx={{ width: '1px', height: 20, backgroundColor: dsColorNew.grayBorder, mx: 0.5 }}/>
			{batch.hasExport && <AppButtonIcon iconWeight="regular" icon="cloud-arrow-down" tooltip="Download Export" onClick={noop}/>}
			<AppButtonIcon iconWeight="solid" icon="clock-rotate-left" tooltip="View Historical" onClick={noop}/>
			<AppButtonIcon iconWeight="regular" icon="file-excel" tooltip="Download Batch Excel" onClick={noop}/>
		</Box>
	</Box>
}


export default function BatchDemoData() {
	// No spacing: the real table sits in a plain Grid container, so the sample keeps its exact width
	return <Grid container>
		<Grid item xs={12}>
			{/* the real table's flat card: 16px padding, 6px corners, a 64px toolbar with the filter icon */}
			<Card sx={{ borderRadius: '6px', border: 'none', boxShadow: 'none' }}>
				<CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
					<Box sx={{ minHeight: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between', pl: 0.5, pr: 3 }}>
						<AppCardTitle icon="clipboard-list" title="Batch List"/>
						<IconButton aria-label="Filter Table" sx={{ alignSelf: 'flex-start', width: 40, height: 40, borderRadius: '8px', color: 'rgb(99, 115, 129)' }}>
							<IconSeeker icon="filter-list" sx={{ fontSize: 18, color: 'inherit' }}/>
						</IconButton>
					</Box>
					{/* the real table's cells: 16px / 10px sides (4px in the header), 13px body, headers on one line */}
					<Box sx={{ overflowX: 'auto', '& .MuiTableCell-root': { px: 1.25 }, '& th.MuiTableCell-root': { py: 0.5 } }}>
						<Table>
							<TableHead>
								<TableRow sx={{ '& th': { textAlign: 'center', whiteSpace: 'nowrap' } }}>
									<TableCell sx={{ width: 42 }}>
										<Checkbox sx={{ color: alpha(dsColorNew.white, 0.7), '&.Mui-checked': { color: dsColorNew.white } }}/>
									</TableCell>
									<TableCell>Batch ID</TableCell>
									<TableCell sx={{ textAlign: 'left !important' }}>Sites</TableCell>
									<TableCell sx={{ textAlign: 'left !important' }}>Offices</TableCell>
									<TableCell>Nº Searches</TableCell>
									<TableCell>Creation Date</TableCell>
									<TableCell sx={{ width: 250 }}>Status</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{DEMO_BATCHES.map((batch, ri) => (
									<TableRow key={ri} sx={{ backgroundColor: ri % 2 === 0 ? dsColorNew.white : dsColorNew.tableRowAlt, '& td': { fontSize: 13, borderColor: dsColorNew.grayBorder } }}>
										<TableCell sx={{ textAlign: 'center' }}>
											<Checkbox sx={{ color: dsColorNew.webBlue500 }}/>
										</TableCell>
										<TableCell>
											{/* the ID is only read whole on hover, as in the real column */}
											<Typography title={String(batch.id)} sx={{ fontSize: 13, textAlign: 'center', maxWidth: 150, mx: 'auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{batch.id}</Typography>
										</TableCell>
										<TableCell><AppRetailerBlockContainer rows={siteRows(batch.sites)}/></TableCell>
										<TableCell><AppOfficesBlockContainer rows={officeRows(batch.offices)}/></TableCell>
										<TableCell sx={{ textAlign: 'center' }}>{batch.searches}</TableCell>
										<TableCell>
											<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', whiteSpace: 'nowrap' }}>
												<Typography sx={{ fontSize: 13 }}>{displayDay(batch.created.date)}</Typography>
												<Typography variant="caption" sx={{ fontSize: 12, color: dsColorNew.textMuted }}>{batch.created.time}</Typography>
											</Box>
										</TableCell>
										<TableCell>
											<StatusCell batch={batch}/>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Box>
				</CardContent>
			</Card>
		</Grid>
	</Grid>
}
